import React, { useEffect, useState } from "react";
import { getMyLimit, LimitUsage } from "../../services/api";

/**
 * How much of today's limit this agent has used.
 *
 * Shown on the booking screen so somebody finds out they are near the ceiling
 * while there is still time to do something about it — not when a sale is
 * refused with a customer standing at the counter.
 *
 * Renders nothing for super users and IT, who are not subject to the limit, and
 * nothing while it is loading: a bar that appears at zero and then jumps looks
 * like the figure changed.
 */
const money = (n: number, currency: string) =>
    `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const DailyLimitBar: React.FC<{ refreshKey?: unknown }> = ({ refreshKey }) => {
    const [limit, setLimit] = useState<LimitUsage | null>(null);

    useEffect(() => {
        let cancelled = false;
        getMyLimit()
            .then((l) => { if (!cancelled) setLimit(l); })
            // A failure here must not get in the way of taking a booking. The
            // server enforces the limit regardless of whether this bar drew.
            .catch(() => { if (!cancelled) setLimit(null); });
        return () => { cancelled = true; };
    }, [refreshKey]);

    if (!limit || !limit.limited) return null;

    const pct = limit.dailyLimit > 0
        ? Math.min(100, Math.round((limit.used / limit.dailyLimit) * 100))
        : 0;

    const tone = limit.blocked
        ? { bar: "bg-red-500", text: "text-red-700", box: "border-red-200 bg-red-50" }
        : limit.warning
            ? { bar: "bg-amber-500", text: "text-amber-800", box: "border-amber-200 bg-amber-50" }
            : { bar: "bg-primary", text: "text-gray-600", box: "border-gray-200 bg-gray-50" };

    return (
        <div className={`rounded-lg border px-3 py-2.5 ${tone.box}`}>
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
                <span className={`text-xs font-medium ${tone.text}`}>
                    {limit.blocked
                        ? "You have reached today's limit"
                        : `Today: ${money(limit.used, limit.currency)} of ${money(limit.dailyLimit, limit.currency)}`}
                </span>
                {!limit.blocked && (
                    <span className="text-xs text-gray-500">
                        {money(limit.remaining, limit.currency)} left
                    </span>
                )}
            </div>

            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div className={`h-full ${tone.bar} transition-all`} style={{ width: `${pct}%` }} />
            </div>

            {limit.blocked && (
                <p className="mt-1.5 text-xs text-red-700">
                    Head office can reset it or raise the limit.
                </p>
            )}
        </div>
    );
};

export default DailyLimitBar;
